import { Sheet, SheetContent, SheetDescription } from '@/components/ui/sheet';
import { X } from 'lucide-react';
import Loader from '@/components/Loader';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { ScrollableTabsList } from '@/components/ScrollableTabsList';
import VendorDetailsContent from './VendorDetailsContent';
import VendorDocumentContent from './VendorDocumentContent';
import { Badge } from '@/components/ui/badge';
import { VendorContactsListContent } from './VendorContactsListContent';
import { useGetVendorById } from '@/services/vendorService';
import { VendorStatus } from '@/types/vendor.types';

export const VendorDetails = ({ onCloseClick, vendorId }: { onCloseClick: () => void; vendorId: string }) => {
  const { data: vendorData, isFetching, isError } = useGetVendorById(vendorId);

  return (
    <Sheet modal={false} open={true}>
      <SheetContent
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] overscroll-auto scrollbar-hide focus:outline-none py-6 px-5"
        tabIndex={-1}
      >
        <Loader isLoading={isFetching}>
          {isError ? (
            <div className="flex h-full items-center relative justify-center w-full text-placeholder">
              <div className="border-[1px] p-[6px] absolute top-0 right-0 rounded-md" onClick={onCloseClick}>
                <X color="#676767" className="h-[20px] w-[20px] cursor-pointer" data-testid="close-button" />
              </div>
              <span>No Results</span>
            </div>
          ) : (
            <div className="flex flex-col gap-4 h-full">
              <div>
                <div className="flex flex-row justify-between align-middle">
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-row gap-2 items-center">
                      <div className="bg-[#9747FF33] rounded-md text-normal font-semibold p-2 h-[48px] min-w-[48px] w-fit flex justify-center text-[#55288F] ">
                        <span className="leading-[32px]">{vendorData?.name[0]}</span>
                      </div>
                      <span className="text-base font-md break-all">{vendorData?.name}</span>
                    </div>
                  </div>
                  <div className="flex flex-row gap-4 h-[32px]">
                    <div className="border-[1px] p-[6px] rounded-md border-bordercolor" onClick={onCloseClick}>
                      <X strokeWidth={1} className="h-[20px] w-[20px] text-placeholder cursor-pointer" />
                    </div>
                  </div>
                </div>
                <div className="flex justify-between w-full pt-2">
                  <div className="text-[#969696] font-[400] flex flex-col text-sm">
                    <span
                      style={{
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {[vendorData?.address1, vendorData?.address2].filter(Boolean).join(', ')}
                    </span>
                    <span>{[vendorData?.city, vendorData?.state, vendorData?.zipCode].filter(Boolean).join(', ')}</span>
                  </div>
                  <div>
                    {vendorData?.status === VendorStatus.ACTIVE ? (
                      <Badge className="bg-[#D5F4E3] text-[#1B8652] hover:bg-[#D5F4E3]">Active</Badge>
                    ) : vendorData?.status === VendorStatus.INACTIVE ? (
                      <Badge className="bg-[#F5F5F5] text-[#676767] hover:bg-[#F5F5F5]">Inactive</Badge>
                    ) : (
                      <Badge className="bg-[#ffeebf] text-[#896010] hover:bg-[#ffeebf]">Pending</Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-hidden w-full">
                <Tabs className="h-full flex flex-col w-full" defaultValue="VENDOR_DETAILS">
                  <ScrollableTabsList>
                    <TabsTrigger className="w-[33.3%]" value="VENDOR_DETAILS" data-testid="trigger-vendor-tab">
                      Vendor Details
                    </TabsTrigger>
                    <TabsTrigger className="w-[33.3%]" value="VENDOR_DOCUMENTS" data-testid="trigger-documents-tab">
                      Documents
                    </TabsTrigger>
                    <TabsTrigger className="w-[33.3%]" value="VENDOR_CONTACTS" data-testid="trigger-contacts-tab">
                      Contact Details
                    </TabsTrigger>
                  </ScrollableTabsList>

                  <TabsContent value="VENDOR_DETAILS" className="flex-1 overflow-y-auto scrollbar-hide">
                    <VendorDetailsContent vendorData={vendorData} />
                  </TabsContent>

                  <TabsContent value="VENDOR_DOCUMENTS" className="flex-1 overflow-y-auto scrollbar-hide">
                    <VendorDocumentContent vendorData={vendorData} />
                  </TabsContent>

                  <TabsContent value="VENDOR_CONTACTS" className="flex-1 overflow-y-auto scrollbar-hide">
                    <VendorContactsListContent contacts={vendorData?.vendorContacts ?? []} />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}

          <SheetDescription />
        </Loader>
      </SheetContent>
    </Sheet>
  );
};
